/**
 * @fileoverview Export functionality for CXGN Table
 *
 * Handles exporting table data to various formats:
 * - CSV download
 * - Excel/XLS download (HTML format)
 * - Copy to clipboard
 * - Print preview
 *
 * Supports server-side processing tables by fetching all data
 * from the server with a loading indicator.
 *
 * @module ExportManager
 */

import { EXPORT_FORMAT, CSS_CLASSES, ICONS, DEFAULTS } from "./constants.js";

/**
 * @typedef {import('./types.js').TableOptions} TableOptions
 * @typedef {import('./types.js').ColumnDefinition} ColumnDefinition
 */

/**
 * ExportManager handles all export operations for tables
 * @class
 */
export class ExportManager {
  /**
   * Create an ExportManager instance
   * @param {Object} config - Configuration
   * @param {TableOptions} config.options - Table options
   * @param {HTMLElement} config.container - Table container
   * @param {Array<ColumnDefinition>} config.columns - Column definitions
   * @param {HTMLElement} [config.tableElement] - Table DOM element
   * @param {Object} [config.dataTable] - DataTables instance
   */
  constructor({ options, container, columns, tableElement, dataTable }) {
    this.options = options;
    this.container = container;
    this.columns = columns;
    this.tableElement = tableElement;
    this.dataTable = dataTable;
    this._isExporting = false;
  }

  /**
   * Add export buttons to the table footer
   * Should be called after table initialization
   */
  addExportButtons() {
    if (!this.options.enableExport) {
      return;
    }

    const exportContainer = this._createExportContainer();
    const buttonGroup = this._createButtonGroup();

    // Add buttons for each enabled format
    const formats = this.options.exportFormats || DEFAULTS.EXPORT_FORMATS;

    if (formats.includes(EXPORT_FORMAT.CSV)) {
      buttonGroup.appendChild(
        this._createButton("CSV", ICONS.SAVE_FILE, () => this.exportToCSV()),
      );
    }

    if (formats.includes(EXPORT_FORMAT.EXCEL)) {
      buttonGroup.appendChild(
        this._createButton("Excel", ICONS.FILE, () => this.exportToExcel()),
      );
    }

    if (formats.includes(EXPORT_FORMAT.COPY)) {
      buttonGroup.appendChild(
        this._createButton("Copy", ICONS.COPY, () => this.copyToClipboard()),
      );
    }

    if (formats.includes(EXPORT_FORMAT.PRINT)) {
      buttonGroup.appendChild(
        this._createButton("Print", ICONS.PRINT, () => this.printTable()),
      );
    }

    exportContainer.appendChild(buttonGroup);
    this._insertExportContainer(exportContainer);
  }

  /**
   * Create export container element
   * @private
   * @returns {HTMLElement}
   */
  _createExportContainer() {
    const container = document.createElement("div");
    container.className = CSS_CLASSES.EXPORT_FOOTER;
    return container;
  }

  /**
   * Create button group container
   * @private
   * @returns {HTMLElement}
   */
  _createButtonGroup() {
    const group = document.createElement("div");
    group.className = CSS_CLASSES.EXPORT_BUTTONS;

    const label = document.createElement("span");
    label.className = CSS_CLASSES.EXPORT_LABEL;
    label.textContent = "Export:";
    group.appendChild(label);

    return group;
  }

  /**
   * Create an export button
   * @private
   * @param {string} format - Format name for aria-label
   * @param {string} iconClass - Icon CSS class
   * @param {function} onClick - Click handler
   * @returns {HTMLElement}
   */
  _createButton(format, iconClass, onClick) {
    const button = document.createElement("button");
    button.className = CSS_CLASSES.EXPORT_BTN;
    button.setAttribute("title", "Export as " + format);
    button.setAttribute("aria-label", "Export table data as " + format);
    button.setAttribute("type", "button");

    const icon = document.createElement("i");
    icon.className = iconClass;
    button.appendChild(icon);

    button.addEventListener("click", onClick);

    return button;
  }

  /**
   * Insert export container into the page
   * @private
   * @param {HTMLElement} exportContainer
   */
  _insertExportContainer(exportContainer) {
    // Find DataTables wrapper
    const wrapper = this.container.closest(
      "." + CSS_CLASSES.DATATABLES_WRAPPER,
    );

    if (!wrapper) {
      // No DataTables wrapper, append after container
      this.container.parentNode.insertBefore(
        exportContainer,
        this.container.nextSibling,
      );
      return;
    }

    // Remove any export footer we inserted previously (e.g. on table re-init
    // with destroy:true) so the export controls are never duplicated.
    wrapper
      .querySelectorAll("." + CSS_CLASSES.EXPORT_FOOTER)
      .forEach((el) => el.remove());

    // Insert the export controls just before the pagination, or at the end of
    // the wrapper if there is none. We intentionally do NOT clone the
    // DataTables info / pagination elements â cloning duplicated the footer.
    const paginateElement = wrapper.querySelector(
      "." + CSS_CLASSES.DATATABLES_PAGINATE,
    );

    if (paginateElement) {
      paginateElement.parentNode.insertBefore(exportContainer, paginateElement);
    } else {
      wrapper.appendChild(exportContainer);
    }
  }

  /**
   * Export table to CSV format
   * @public
   */
  async exportToCSV() {
    if (this._isExporting) return;

    try {
      this._isExporting = true;
      this._showExportLoading("Preparing CSV export...");

      const data = await this._getExportData();
      const csv = this._generateCSV(data);
      this._downloadFile(csv, "csv");

      console.log("ExportManager: Exported", data.length, "rows to CSV");
    } catch (error) {
      this._handleExportError("Failed to export CSV", error);
    } finally {
      this._hideExportLoading();
      this._isExporting = false;
    }
  }

  /**
   * Export table to Excel format (uses CSV which Excel opens natively)
   * @public
   */
  async exportToExcel() {
    if (this._isExporting) return;

    try {
      this._isExporting = true;
      this._showExportLoading("Preparing Excel export...");

      const data = await this._getExportData();
      const csv = this._generateCSV(data);
      this._downloadFile(csv, "excel"); // Downloads as .xlsx, Excel opens CSV fine

      console.log("ExportManager: Exported", data.length, "rows to Excel (CSV format)");
    } catch (error) {
      this._handleExportError("Failed to export Excel", error);
    } finally {
      this._hideExportLoading();
      this._isExporting = false;
    }
  }

  /**
   * Copy table data to clipboard
   * @public
   */
  async copyToClipboard() {
    if (this._isExporting) return;

    try {
      this._isExporting = true;
      this._showExportLoading("Copying to clipboard...");

      const data = await this._getExportData();
      const tsv = this._generateTSV(data);

      // Try modern Clipboard API first
      if (navigator.clipboard && navigator.clipboard.writeText) {
        await navigator.clipboard.writeText(tsv);
        console.log("ExportManager: Copied", data.length, "rows to clipboard");
      } else {
        // Fallback to older method
        this._copyToClipboardFallback(tsv);
      }
    } catch (error) {
      this._handleExportError("Failed to copy to clipboard", error);
    } finally {
      this._hideExportLoading();
      this._isExporting = false;
    }
  }

  /**
   * Print table
   * @public
   */
  async printTable() {
    if (this._isExporting) return;

    try {
      this._isExporting = true;
      this._showExportLoading("Preparing print preview...");

      const data = await this._getExportData();
      const html = this._generatePrintHTML(data);
      const printWindow = window.open("", "", "height=400,width=600");
      printWindow.document.write(html);
      printWindow.document.close();
      printWindow.print();

      console.log("ExportManager: Printed", data.length, "rows");
    } catch (error) {
      this._handleExportError("Failed to print table", error);
    } finally {
      this._hideExportLoading();
      this._isExporting = false;
    }
  }

  /**
   * Get data to export
   * For server-side tables, fetches ALL data from the server
   * @private
   * @returns {Promise<Array>}
   */
  async _getExportData() {
    // Check if this is a server-side processed table
    const isServerSide = this._isServerSideTable();

    if (isServerSide) {
      console.log("ExportManager: Fetching all data from server for export...");
      return await this._fetchAllServerSideData();
    }

    // For client-side tables, get data from DataTables instance
    if (this.dataTable) {
      return this.dataTable.rows({ search: "applied" }).data().toArray();
    }

    // Extract from DOM table
    return this._extractDataFromDOM();
  }

  /**
   * Check if the table uses server-side processing
   * @private
   * @returns {boolean}
   */
  _isServerSideTable() {
    // Check options first
    if (this.options.serverSide === true) {
      return true;
    }

    // Check DataTable settings if available
    if (this.dataTable && this.dataTable.settings) {
      try {
        const settings = this.dataTable.settings()[0];
        return settings && settings.oFeatures && settings.oFeatures.bServerSide;
      } catch (e) {
        // Ignore errors
      }
    }

    return false;
  }

  /**
   * Fetch ALL data from server for export
   * Makes a request with length=-1 to get all rows
   * @private
   * @returns {Promise<Array>}
   */
  async _fetchAllServerSideData() {
    // Get the ajax URL from options or DataTable settings
    let ajaxUrl = this._getAjaxUrl();

    if (!ajaxUrl) {
      console.warn(
        "ExportManager: No AJAX URL found, falling back to current page data",
      );
      return this.dataTable
        ? this.dataTable.rows({ search: "applied" }).data().toArray()
        : [];
    }

    try {
      // Build export request parameters
      const params = this._buildExportParams();

      // Make the request
      const url = new URL(ajaxUrl, window.location.origin);
      Object.keys(params).forEach((key) =>
        url.searchParams.append(key, params[key]),
      );

      console.log("ExportManager: Fetching from", url.toString());

      const response = await fetch(url.toString(), {
        method: "GET",
        headers: {
          Accept: "application/json",
        },
      });

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }

      const json = await response.json();
      const data = json.data || json.aaData || [];

      console.log(
        "ExportManager: Fetched",
        data.length,
        "rows (total:",
        json.recordsTotal || json.iTotalRecords || "unknown",
        ")",
      );

      return data;
    } catch (error) {
      console.error("ExportManager: Error fetching server data:", error);
      throw error;
    }
  }

  /**
   * Get the AJAX URL from options or DataTable settings
   * @private
   * @returns {string|null}
   */
  _getAjaxUrl() {
    // Check for explicit export endpoint
    if (this.options.exportEndpoint) {
      return this.options.exportEndpoint;
    }

    // Get from options.ajax
    if (this.options.ajax) {
      if (typeof this.options.ajax === "string") {
        return this.options.ajax;
      }
      if (this.options.ajax.url) {
        return this.options.ajax.url;
      }
    }

    // Try to get from DataTable settings
    if (this.dataTable && this.dataTable.settings) {
      try {
        const settings = this.dataTable.settings()[0];
        if (settings && settings.ajax) {
          if (typeof settings.ajax === "string") {
            return settings.ajax;
          }
          if (settings.ajax.url) {
            return settings.ajax.url;
          }
        }
      } catch (e) {
        // Ignore errors
      }
    }

    return null;
  }

  /**
   * Build parameters for export request
   * Uses DataTables format with length=1000000 to get all rows
   * @private
   * @returns {Object}
   */
  _buildExportParams() {
    const params = {
      start: 0,
      length: 1000000, // Large number to get all rows
      draw: 1,
    };

    // Add current search term if any
    if (this.dataTable) {
      const searchTerm = this.dataTable.search();
      if (searchTerm) {
        params["search[value]"] = searchTerm;
        params["search[regex]"] = false;
      }

      // Add current order
      const order = this.dataTable.order();
      if (order && order.length > 0) {
        order.forEach((o, i) => {
          params[`order[${i}][column]`] = o[0];
          params[`order[${i}][dir]`] = o[1];
        });
      }

      // Add column data for proper server-side handling
      try {
        const settings = this.dataTable.settings()[0];
        const columns = settings.aoColumns;
        if (columns) {
          columns.forEach((col, i) => {
            // Use column index if data is null/undefined
            const colData = col.data !== null && col.data !== undefined ? col.data : i;
            params[`columns[${i}][data]`] = colData;
            params[`columns[${i}][name]`] = col.sName || "";
            params[`columns[${i}][searchable]`] = col.bSearchable;
            params[`columns[${i}][orderable]`] = col.bSortable;
            params[`columns[${i}][search][value]`] = "";
            params[`columns[${i}][search][regex]`] = false;
          });
        }
      } catch (e) {
        console.warn("ExportManager: Could not get column info:", e);
      }
    }

    return params;
  }

  /**
   * Extract data from DOM table
   * @private
   * @returns {Array}
   */
  _extractDataFromDOM() {
    const data = [];

    if (!this.tableElement) {
      return data;
    }

    const rows = this.tableElement.querySelectorAll("tbody tr");
    rows.forEach((row) => {
      const cells = row.querySelectorAll("td");
      const rowData = Array.from(cells).map((cell) => cell.textContent.trim());
      data.push(rowData);
    });

    return data;
  }

  /**
   * Columns that take part in an export, in order.
   * Hidden columns carry no visible value, and columns marked
   * `exportable: false` (row action buttons, for example) render controls
   * rather than data, so neither belongs in a CSV/print output. Headers and
   * row values must both go through this so they stay aligned.
   * @private
   * @returns {Array<ColumnDefinition>}
   */
  _exportColumns() {
    if (!this.columns) {
      return [];
    }
    return this.columns.filter(
      (col) => col.visible !== false && col.exportable !== false,
    );
  }

  /**
   * Extract headers from columns or DOM
   * @private
   * @returns {Array}
   */
  _extractHeaders() {
    // Use column titles if available
    if (this.columns && this.columns.length > 0) {
      return this._exportColumns().map((col) => col.title || col.data || "");
    }

    // Extract from DOM
    if (this.tableElement) {
      const headers = this.tableElement.querySelectorAll("thead th");
      return Array.from(headers).map((th) => th.textContent.trim());
    }

    return [];
  }

  /**
   * Extract row values from data
   * @private
   * @param {Array|Object} row - Row data
   * @returns {Array}
   */
  _extractRowValues(row) {
    if (Array.isArray(row)) {
      // If we have column definitions, use them to extract values
      if (this.columns && this.columns.length > 0) {
        return this._exportColumns()
          .map((col, colIndex) => {
            // First try numeric index if col.data is a number
            let index = col.data;
            if (typeof col.data === "string") {
              // Try to parse as number for array index
              const parsed = parseInt(col.data, 10);
              if (!isNaN(parsed)) {
                index = parsed;
              } else {
                // If not a number, use column index position
                index = colIndex;
              }
            }
            const value = row[index] !== undefined ? row[index] : "";
            return this._cellExportValue(col, value, row);
          });
      }
      // No column definitions, include all values
      return row.map((val) => this._stripHTML(String(val)));
    }

    if (typeof row === "object" && this.columns) {
      return this._exportColumns()
        .map((col) => {
          const value = row[col.data];
          return this._cellExportValue(col, value, row);
        });
    }

    return [];
  }

  /**
   * Resolve the exported text for a single cell.
   * Columns are frequently defined as { data: null, render: fn } where the
   * visible value is produced by a DataTables render function (e.g. link
   * cells). In that case the raw model value is null/undefined, so we run the
   * render function (display type) and strip the resulting HTML. Falls back to
   * the raw value if there is no renderer or it throws.
   * @private
   * @param {Object} col - Column definition
   * @param {*} baseValue - Raw model value for the cell
   * @param {Array|Object} row - Full row data (passed to the renderer)
   * @returns {string}
   */
  _cellExportValue(col, baseValue, row) {
    if (col && typeof col.render === "function") {
      try {
        const rendered = col.render(baseValue, "display", row, {});
        if (rendered !== null && rendered !== undefined) {
          return this._stripHTML(String(rendered));
        }
      } catch (e) {
        // Fall through to the raw value below
      }
    }
    return this._stripHTML(
      String(baseValue !== undefined && baseValue !== null ? baseValue : "")
    );
  }

  /**
   * Generate CSV content
   * @private
   * @param {Array} data - Data to export
   * @returns {string}
   */
  _generateCSV(data) {
    const rows = [];

    // Add headers
    const headers = this._extractHeaders();
    if (headers.length > 0) {
      rows.push(headers.map((h) => this._escapeCSV(h)).join(","));
    }

    // Add data rows
    data.forEach((row) => {
      const values = this._extractRowValues(row);
      rows.push(values.map((v) => this._escapeCSV(v)).join(","));
    });

    return rows.join("\n");
  }

  /**
   * Generate TSV content (tab-separated for clipboard)
   * @private
   * @param {Array} data - Data to export
   * @returns {string}
   */
  _generateTSV(data) {
    const rows = [];

    // Add headers
    const headers = this._extractHeaders();
    if (headers.length > 0) {
      rows.push(headers.join("\t"));
    }

    // Add data rows
    data.forEach((row) => {
      const values = this._extractRowValues(row);
      rows.push(values.join("\t"));
    });

    return rows.join("\n");
  }

  /**
   * Generate Excel HTML content
   * @private
   * @param {Array} data - Data to export
   * @returns {string}
   */
  _generateExcelHTML(data) {
    // Build HTML table for Excel - use simple string escaping
    const headers = this._extractHeaders();
    const escapeForExcel = (val) => {
      if (val === null || val === undefined) return "";
      return String(val)
        .replace(/&/g, "&amp;")
        .replace(/</g, "&lt;")
        .replace(/>/g, "&gt;")
        .replace(/"/g, "&quot;")
        .replace(/[\r\n]+/g, " ");
    };

    // Build header row
    const headerCells = headers.map(h => "<th>" + escapeForExcel(h) + "</th>").join("");

    // Build data rows
    const rowStrings = [];
    for (let i = 0; i < data.length; i++) {
      const row = data[i];
      const values = this._extractRowValues(row);
      let rowHtml = "<tr>";
      for (let j = 0; j < values.length; j++) {
        rowHtml += "<td>" + escapeForExcel(values[j]) + "</td>";
      }
      rowHtml += "</tr>";
      rowStrings.push(rowHtml);
    }

    return `<html xmlns:o="urn:schemas-microsoft-com:office:office" xmlns:x="urn:schemas-microsoft-com:office:excel" xmlns="http://www.w3.org/TR/REC-html40">
<head>
<meta http-equiv="Content-Type" content="text/html; charset=utf-8">
<!--[if gte mso 9]><xml><x:ExcelWorkbook><x:ExcelWorksheets><x:ExcelWorksheet><x:Name>Sheet1</x:Name><x:WorksheetOptions><x:DisplayGridlines/></x:WorksheetOptions></x:ExcelWorksheet></x:ExcelWorksheets></x:ExcelWorkbook></xml><![endif]-->
</head>
<body>
<table border="1">
<thead>
<tr>${headerCells}</tr>
</thead>
<tbody>
${rowStrings.join("\n")}
</tbody>
</table>
</body>
</html>`;
  }

  /**
   * Generate print HTML content
   * @private
   * @param {Array} data - Data to export
   * @returns {string}
   */
  _generatePrintHTML(data) {
    const title = this.options.exportTitle || "Table Data";
    const css =
      "<style>body { font-family: Arial, sans-serif; margin: 20px; } h1 { color: #333; } table { width: 100%; border-collapse: collapse; margin-top: 20px; } th, td { border: 1px solid #ddd; padding: 12px; text-align: left; } th { background-color: #f2f2f2; font-weight: bold; } tr:nth-child(even) { background-color: #f9f9f9; } @media print { body { margin: 0; } }</style>";

    let html = '<html><head><meta charset="utf-8">' + css + "</head><body>";
    html += "<h1>" + title + "</h1>";
    html += "<table>";

    // Add headers
    html += "<thead><tr>";
    const headers = this._extractHeaders();
    headers.forEach((header) => {
      html += "<th>" + header + "</th>";
    });
    html += "</tr></thead>";

    // Add data rows
    html += "<tbody>";
    data.forEach((row) => {
      html += "<tr>";
      const values = this._extractRowValues(row);
      values.forEach((value) => {
        html += "<td>" + value + "</td>";
      });
      html += "</tr>";
    });
    html += "</tbody>";

    html += "</table></body></html>";
    return html;
  }

  /**
   * Escape CSV values
   * @private
   * @param {string} value - Value to escape
   * @returns {string}
   */
  _escapeCSV(value) {
    if (value === null || value === undefined) {
      return "";
    }

    const str = String(value);

    // Check if value needs to be quoted
    if (str.includes(",") || str.includes('"') || str.includes("\n")) {
      return '"' + str.replace(/"/g, '""') + '"';
    }

    return str;
  }

  /**
   * Escape HTML entities
   * @private
   * @param {string} value - Value to escape
   * @returns {string}
   */
  _escapeHTML(value) {
    if (value === null || value === undefined) {
      return "";
    }

    const div = document.createElement("div");
    div.textContent = String(value);
    return div.innerHTML;
  }

  /**
   * Strip HTML tags from value
   * @private
   * @param {string} value - Value to clean
   * @returns {string}
   */
  _stripHTML(value) {
    if (value === null || value === undefined) {
      return "";
    }

    const tmp = document.createElement("div");
    tmp.innerHTML = String(value);
    return tmp.textContent || tmp.innerText || "";
  }

  /**
   * Copy to clipboard fallback for older browsers
   * @private
   * @param {string} text - Text to copy
   */
  _copyToClipboardFallback(text) {
    const textarea = document.createElement("textarea");
    textarea.value = text;
    document.body.appendChild(textarea);
    textarea.select();
    document.execCommand("copy");
    document.body.removeChild(textarea);
  }

  /**
   * Download file
   * @private
   * @param {string} content - File content
   * @param {string} format - File format
   */
  _downloadFile(content, format) {
    // Determine filename and mime type based on format
    let filename, mimeType;

    switch (format) {
      case "excel":
      case "csv":
        // Both use CSV format - Excel opens CSV natively
        filename = (this.options.exportFileName || "export") + ".csv";
        mimeType = "text/csv;charset=utf-8";
        break;
      default:
        filename = (this.options.exportFileName || "export") + "." + format;
        mimeType = "text/plain";
    }

    // Add BOM for Excel UTF-8 compatibility
    const bom = "\uFEFF";
    const blob = new Blob([bom + content], { type: mimeType });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    window.URL.revokeObjectURL(url);
    document.body.removeChild(a);
  }

  /**
   * Show export loading indicator
   * Uses the same styling as DataTables processing indicator
   * @private
   * @param {string} message - Loading message to display
   */
  _showExportLoading(message = "Exporting data...") {
    // Find the DataTables wrapper
    const wrapper = this.container.closest(".dataTables_wrapper");

    if (wrapper) {
      // Add processing class for backdrop
      wrapper.classList.add("processing");

      // Find or create the processing indicator
      let processingDiv = wrapper.querySelector(".dataTables_processing");

      if (!processingDiv) {
        processingDiv = document.createElement("div");
        processingDiv.className = "dataTables_processing";
        wrapper.insertBefore(processingDiv, wrapper.firstChild);
      }

      // Update message and show
      processingDiv.textContent = message;
      processingDiv.style.display = "block";

      this._exportLoadingElement = processingDiv;
      this._exportWrapper = wrapper;
    } else {
      // Fallback: create a simple overlay
      const overlay = document.createElement("div");
      overlay.className = "cxgn-export-loading";
      overlay.innerHTML = `
        <div class="cxgn-export-loading-content">
          <div class="spinner"></div>
          <p>${message}</p>
        </div>
      `;
      overlay.style.cssText = `
        position: fixed;
        top: 0;
        left: 0;
        right: 0;
        bottom: 0;
        background: rgba(255, 255, 255, 0.9);
        display: flex;
        align-items: center;
        justify-content: center;
        z-index: 9999;
      `;

      document.body.appendChild(overlay);
      this._exportLoadingElement = overlay;
    }
  }

  /**
   * Hide export loading indicator
   * @private
   */
  _hideExportLoading() {
    if (this._exportWrapper) {
      this._exportWrapper.classList.remove("processing");
    }

    if (this._exportLoadingElement) {
      // If it's the DataTables processing div, just hide it
      if (this._exportLoadingElement.classList.contains("dataTables_processing")) {
        this._exportLoadingElement.style.display = "none";
      } else {
        // Remove the overlay
        this._exportLoadingElement.remove();
      }

      this._exportLoadingElement = null;
      this._exportWrapper = null;
    }
  }

  /**
   * Get filename for export
   * @private
   * @returns {string}
   */
  _getFileName() {
    return this.options.exportFileName || "export";
  }

  /**
   * Handle export errors
   * @private
   * @param {string} message - Error message
   * @param {Error} error - Error object
   */
  _handleExportError(message, error) {
    console.error("ExportManager:", message, error);

    // Try showError if available (Bootstrap modal)
    if (typeof showError === "function") {
      showError(message + ": " + error.message);
    } else {
      // Fallback to alert
      alert(message + ": " + error.message);
    }
  }
}
